import { useEffect, useState, type KeyboardEvent } from 'react';
import type { tickets } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Card, CopyButton, Group, Pill, SimpleGrid, Stack, Text } from '@repletoryreach/sdk-ui';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { StatusTag, TicketNumber, TypeTag } from '../components/TicketBits';
import { categoryLabel, channelLabel, personName, statusesForType, type Lookups } from '../lib/data';
import { fromLocalInput, toLocalInput } from '../lib/time';

const MUTED = 'var(--rr-color-text-muted)';

/** The ticket's header: number, subject, and every picker that changes it. */
export function TicketHeader({ ticket, lk, tz, busy, onPatch, onMerge }: {
  ticket: tickets.Ticket;
  lk: Lookups;
  tz: string;
  busy: boolean;
  onPatch: (patch: tickets.UpdateTicketRequest) => void;
  onMerge: () => void;
}) {
  const t = useT();
  const { user } = useAuth();
  const [subject, setSubject] = useState(ticket.subject);
  const [tag, setTag] = useState('');
  const [due, setDue] = useState(toLocalInput(ticket.due_at, tz));

  useEffect(() => setSubject(ticket.subject), [ticket.subject]);
  useEffect(() => setDue(toLocalInput(ticket.due_at, tz)), [ticket.due_at, tz]);

  const statuses = statusesForType(lk.statuses, ticket.type.id);
  const merged = !!ticket.merged_into_id;

  function saveSubject() {
    const v = subject.trim();
    if (!v) { setSubject(ticket.subject); return; }
    if (v !== ticket.subject) onPatch({ subject: v });
  }

  function addTag() {
    const v = tag.trim();
    if (!v) return;
    if (!ticket.tags.some((x) => x.toLowerCase() === v.toLowerCase())) onPatch({ tags: [...ticket.tags, v] });
    setTag('');
  }

  function changeType(id: string) {
    const next = lk.typeById.get(id);
    if (!next || id === ticket.type.id) return;
    openConfirmModal({
      title: t('tickets.detail.changeType.title'),
      children: <Text role="bodySmall">{t('tickets.detail.changeType.body', { from: ticket.type.name, to: next.name })}</Text>,
      labels: { confirm: t('tickets.detail.changeType.confirm'), cancel: t('action.cancel') },
      onConfirm: () => onPatch({ type_id: id })
    });
  }

  const people = [
    { value: '', label: t('tickets.unassigned') },
    ...(user ? [{ value: user.id, label: t('tickets.me') }] : []),
    ...lk.people.filter((p) => p.id !== user?.id).map((p) => ({ value: p.id, label: personName(p) }))
  ];
  if (ticket.assignee_user_id && !people.some((p) => p.value === ticket.assignee_user_id)) {
    people.push({ value: ticket.assignee_user_id, label: t('common.unknown') });
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group gap="sm" justify="space-between" wrap="wrap">
          <Group gap="sm" wrap="wrap" align="center">
            <TicketNumber number={ticket.number} />
            <CopyButton value={ticket.number}>
              {({ copied, failed, copy }) => (
                <Button size="xs" variant="subtle" onClick={copy}>
                  {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('tickets.detail.copyNumber')}
                </Button>
              )}
            </CopyButton>
            <StatusTag status={ticket.status} size="sm" />
            <TypeTag type={ticket.type} />
          </Group>
          {!merged && <Button size="xs" variant="default" onClick={onMerge}>{t('tickets.merge.action')}</Button>}
        </Group>

        <TextInput
          aria-label={t('tickets.field.subject')}
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          onBlur={saveSubject}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); } }}
          maxLength={200}
          disabled={merged}
          className="rr-tickets__subject-input"
        />
        <Text role="caption" c={MUTED}>
          {t('tickets.detail.opened', {
            when: formatDateTime(ticket.created_at),
            client: ticket.contact?.name || ticket.contact?.ref || t('tickets.noClient'),
            channel: channelLabel(t, ticket.channel)
          })}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
          <Select
            label={t('common.status')}
            value={ticket.status.id}
            disabled={busy || merged}
            onChange={(e) => onPatch({ status_id: e.currentTarget.value })}
            data={statuses.map((s) => {
              // The category only when the business's own name does not already say it ("New (New)").
              const cat = categoryLabel(t, s.category);
              return { value: s.id, label: s.name.trim().toLowerCase() === cat.trim().toLowerCase() ? s.name : `${s.name} (${cat})` };
            })}
          />
          <Select
            label={t('tickets.field.priority')}
            value={ticket.priority.id}
            disabled={busy || merged}
            onChange={(e) => onPatch({ priority_id: e.currentTarget.value })}
            data={lk.priorities.map((p) => ({ value: p.id, label: p.name }))}
          />
          <Select
            label={t('tickets.field.type')}
            value={ticket.type.id}
            disabled={busy || merged}
            onChange={(e) => changeType(e.currentTarget.value)}
            data={lk.types.filter((x) => x.active || x.id === ticket.type.id).map((x) => ({ value: x.id, label: x.name }))}
          />
          <Select
            label={t('tickets.field.team')}
            value={ticket.team_id ?? ''}
            disabled={busy || merged}
            onChange={(e) => onPatch({ team_id: e.currentTarget.value || null })}
            data={[{ value: '', label: t('tickets.noTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]}
          />
          <Select
            label={t('tickets.field.assignee')}
            value={ticket.assignee_user_id ?? ''}
            disabled={busy || merged}
            onChange={(e) => onPatch({ assignee_user_id: e.currentTarget.value || null })}
            data={people}
          />
          <TextInput
            type="datetime-local"
            label={t('tickets.field.due')}
            value={due}
            disabled={busy || merged}
            onChange={(e) => setDue(e.currentTarget.value)}
            onBlur={() => {
              const next = fromLocalInput(due, tz);
              if ((next ?? null) !== (ticket.due_at ? fromLocalInput(toLocalInput(ticket.due_at, tz), tz) : null)) onPatch({ due_at: next });
            }}
            description={t('tickets.field.dueHint', { zone: tz })}
          />
        </SimpleGrid>

        <Stack gap="xs">
          <Text role="inputLabel">{t('tickets.field.tags')}</Text>
          <Group gap="xs" wrap="wrap">
            {ticket.tags.length === 0 && <Text role="caption" c={MUTED}>{t('tickets.detail.noTags')}</Text>}
            {ticket.tags.map((x) => (
              <Pill key={x} withRemoveButton={!merged} removeLabel={t('tickets.detail.removeTag', { tag: x })}
                onRemove={() => onPatch({ tags: ticket.tags.filter((y) => y !== x) })}>
                {x}
              </Pill>
            ))}
            {!merged && (
              <TextInput
                size="xs"
                aria-label={t('tickets.detail.addTag')}
                placeholder={t('tickets.detail.addTag')}
                value={tag}
                maxLength={60}
                onChange={(e) => setTag(e.currentTarget.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                onBlur={addTag}
              />
            )}
          </Group>
        </Stack>
      </Stack>
    </Card>
  );
}
